import React, { useState } from 'react';
import { Eye, EyeOff, User, Lock, AlertCircle, CheckCircle } from 'lucide-react';
import { toast } from 'react-toastify';
import { useTranslation } from 'react-i18next';
import api from '../../services/apiClient.ts';
import { useTheme } from '../../contexts/ThemeContext';
import { useLanguage } from '../../contexts/LanguageContext';
import qs from "qs"; // لازم نثبته

const Login = ({ onLogin }) => {
  const { t } = useTranslation();
  const { setTheme } = useTheme();
  const { changeLanguage } = useLanguage();
  const [formData, setFormData] = useState({
    username: 'dev@acme.test',
    password: 'secret123'
  });
  const [showPassword, setShowPassword] = useState(false);
  const [errors, setErrors] = useState({});
  const [isLoading, setIsLoading] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const validateForm = () => {
    const newErrors = {};
    if (!formData.username.trim()) {
      newErrors.username = t('auth.usernameRequired');
    }
    if (!formData.password) {
      newErrors.password = t('auth.passwordRequired');
    } else if (formData.password.length < 6) {
      newErrors.password = t('auth.passwordMinLength');
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;

    setIsLoading(true);
    try {
      const res = await api.post(
        "/v1/auth/login",
        qs.stringify({
          email: formData.username,  // Laravel يتوقع email
          password: formData.password,
        }),
        {
          headers: {
            "X-Tenant": "default-tenant-1",
            "Content-Type": "application/x-www-form-urlencoded",
          },
        }
      );

      const token = res.data.token;
      // خزن التينانت للمستقبل (يا إما من الـ response إذا يرجع key، أو ثابت)
      const tenantKey = res.data?.tenant?.key || "default-tenant-1";
      localStorage.setItem("tenant", tenantKey);
      
      const branchId = res.data?.user?.branch_id || null;
      if (branchId) {
        localStorage.setItem("branch", branchId);
      }

      localStorage.setItem("auth_token", token);

      // Save current user key and apply any saved preferences for this user
      const emailKey = (formData.username || '').trim().toLowerCase();
      if (emailKey) {
        localStorage.setItem('user_email', emailKey);
        const savedTheme = localStorage.getItem(`prefs:${emailKey}:theme`) || localStorage.getItem('theme');
        const savedLang = localStorage.getItem(`prefs:${emailKey}:lang`) || localStorage.getItem('i18nextLng');

        if (savedTheme) {
          setTheme(savedTheme);
        }
        if (savedLang) {
          changeLanguage(savedLang);
        }
      }

      toast.success(t("auth.loginSuccess"));
      if (onLogin) onLogin();
      window.location.href = "/"; // رجع للداشبورد
    } catch (err) {
      console.error("Login error:", err);
      toast.error(t("auth.invalidCredentials"));
    } finally {
      setIsLoading(false);
    }
  };

  const handleTogglePasswordVisibility = () => {
    setShowPassword(!showPassword);
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-primary-50 via-white to-secondary-50 dark:from-gray-900 dark:via-gray-800 dark:to-gray-900 flex items-center justify-center px-4 sm:px-6 lg:px-8">
      <div className="max-w-md w-full space-y-8">
        {/* Header */}
        <div className="text-center">
          <img
            src="/ajaba-logo.png"
            alt="App Logo"
            className="mx-auto h-64 w-64  mb-6 object-contain"
          />
          <h1 className="text-4xl font-bold text-gray-900 dark:text-gray-100 mb-2">
            {t('auth.appName')}
          </h1>
          <p className="text-lg text-gray-600 dark:text-gray-400">
            {t('auth.appSubtitle')}
          </p>
        </div>

        {/* Login Form */}
        <div className="card dark:bg-gray-800 dark:border-gray-700 p-8">
          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Username Field */}
            <div>
              <label htmlFor="username" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                {t('auth.username')}
              </label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 rtl:left-auto rtl:right-0 pl-3 rtl:pl-0 rtl:pr-3 flex items-center pointer-events-none">
                  <User className="h-5 w-5 text-gray-400 dark:text-gray-500" />
                </div>
                <input
                  id="username"
                  name="username"
                  type="text"
                  autoComplete="username"
                  required
                  autoFocus
                  value={formData.username}
                  onChange={handleChange}
                  className={`input-field pl-10 rtl:pl-3 rtl:pr-10 dark:bg-gray-700 dark:border-gray-600 dark:text-gray-100 dark:placeholder-gray-400 ${
                    errors.username ? 'border-red-300 focus:ring-red-500 dark:border-red-600' : ''
                  }`}
                  placeholder={t('auth.usernamePlaceholder')}
                />
              </div>
              {errors.username && (
                <div className="mt-2 flex items-center text-sm text-red-600 dark:text-red-400">
                  <AlertCircle className="h-4 w-4 mr-1 rtl:mr-0 rtl:ml-1" />
                  {errors.username}
                </div>
              )}
            </div>

            {/* Password Field */}
            <div>
              <label htmlFor="password" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                {t('auth.password')}
              </label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 rtl:left-auto rtl:right-0 pl-3 rtl:pl-0 rtl:pr-3 flex items-center pointer-events-none">
                  <Lock className="h-5 w-5 text-gray-400 dark:text-gray-500" />
                </div>
                <input
                  id="password"
                  name="password"
                  type={showPassword ? 'text' : 'password'}
                  autoComplete="current-password"
                  required
                  value={formData.password}
                  onChange={handleChange}
                  className={`input-field pl-10 pr-10 rtl:pl-10 rtl:pr-10 dark:bg-gray-700 dark:border-gray-600 dark:text-gray-100 dark:placeholder-gray-400 ${
                    errors.password ? 'border-red-300 focus:ring-red-500 dark:border-red-600' : ''
                  }`}
                  placeholder={t('auth.passwordPlaceholder')}
                />
                <button
                  type="button"
                  className="absolute inset-y-0 right-0 rtl:right-auto rtl:left-0 pr-3 rtl:pr-0 rtl:pl-3 flex items-center"
                  onClick={handleTogglePasswordVisibility}
                >
                  {showPassword ? (
                    <EyeOff className="h-5 w-5 text-gray-400 dark:text-gray-500 hover:text-gray-600 dark:hover:text-gray-400 transition-colors" />
                  ) : (
                    <Eye className="h-5 w-5 text-gray-400 dark:text-gray-500 hover:text-gray-600 dark:hover:text-gray-400 transition-colors" />
                  )}
                </button>
              </div>
              {errors.password && (
                <div className="mt-2 flex items-center text-sm text-red-600 dark:text-red-400">
                  <AlertCircle className="h-4 w-4 mr-1 rtl:mr-0 rtl:ml-1" />
                  {errors.password}
                </div>
              )}
            </div>

            {/* Submit Button */}
            <button
              type="submit"
              disabled={isLoading}
              className={`w-full flex justify-center items-center py-3 px-4 border border-transparent rounded-lg shadow-sm text-sm font-medium text-white bg-gradient-to-r from-primary-600 to-secondary-600 hover:from-primary-700 hover:to-secondary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 dark:focus:ring-offset-gray-800 focus:ring-primary-500 transition-all duration-200 ${
                isLoading ? 'opacity-75 cursor-not-allowed' : ''
              }`}
            >
              {isLoading ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2 rtl:mr-0 rtl:ml-2"></div>
                  {t('auth.signingIn')}
                </>
              ) : (
                t('auth.signIn')
              )}
            </button>

            {/* Demo Info */}
            <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-4">
              <div className="flex items-start">
                <CheckCircle className="h-5 w-5 text-blue-600 dark:text-blue-400 mt-0.5 mr-3 rtl:mr-0 rtl:ml-3 flex-shrink-0" />
                <div>
                  <h3 className="text-sm font-medium text-blue-800 dark:text-blue-300 mb-1">
                    {t('auth.demoMode')}
                  </h3>
                  <p className="text-sm text-blue-700 dark:text-blue-400">
                    {t('auth.demoInstructions')}
                  </p>
                </div>
              </div>
            </div>
          </form>
        </div>

        {/* Footer */}
        <div className="text-center">
          <p className="text-sm text-gray-500">
            {t('auth.appTagline')}
          </p>
        </div>
      </div>
    </div>
  );
};

export default Login;
